import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Link as MuiLink,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/axios';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/users/login', formData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setLoading(false);
      
      // Redirect based on user type
      if (response.data.user.userType === 'venue') {
        navigate('/venue/dashboard');
      } else if (response.data.user.userType === 'dj') {
        navigate('/dj/dashboard');
      } else {
        // Fallback if userType is not recognized
        navigate('/');
      }
    } catch (err) {
      if (err.response) {
        if (err.response.status === 401) {
          setError('Invalid email or password.');
        } else {
          setError(err.response.data?.error || 'Login failed. Please try again.');
        }
      } else {
        setError('Login failed. Please check your connection.');
      }
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: isMobile ? 4 : 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: isMobile ? 2 : 0,
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: isMobile ? 3 : 4, 
            width: '100%',
            borderRadius: isMobile ? 2 : 3,
          }}
        >
          <Typography 
            component="h1" 
            variant={isMobile ? "h5" : "h4"} 
            align="center"
            gutterBottom
          >
            Sign in to SetSync
          </Typography>
          {error && (
            <Typography 
              color="error" 
              align="center" 
              sx={{ 
                mt: 2,
                fontSize: isMobile ? '0.9rem' : '1rem',
              }}
            >
              {error}
            </Typography>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              inputProps={{
                style: { 
                  fontSize: isMobile ? '0.95rem' : '1rem',
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              inputProps={{
                style: { 
                  fontSize: isMobile ? '0.95rem' : '1rem',
                }
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2,
                py: isMobile ? 1 : 1.5,
                fontSize: isMobile ? '0.9rem' : '1rem',
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <MuiLink 
                component={Link} 
                to="/forgot-password" 
                variant="body2"
                sx={{
                  fontSize: isMobile ? '0.85rem' : '0.875rem',
                  display: 'inline-block',
                  padding: isMobile ? '8px 0' : '4px 0', // Larger tap target on mobile
                }}
              >
                Forgot password?
              </MuiLink>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 